/**
 * AuthContext.js
 *
 * Centralised authentication state for the NDORFlow app.
 *
 * Responsibilities:
 *   - Listen for Supabase auth state changes (login, logout, token refresh)
 *   - Track whether the user has completed onboarding (profile + consents)
 *   - Expose signUp, signIn, signOut, resendConfirmation, refreshProfile methods
 *   - Map raw Supabase error strings to user-friendly messages
 *
 * Onboarding completeness:
 *   A user is "onboarded" when BOTH a profiles row AND at least one consents
 *   row exist. This prevents users from being routed to /dashboard before
 *   finishing all onboarding steps.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { apiFetch } from '@/lib/api';
import { identify, resetAnalytics, track } from '@/lib/analytics';
import { clearEmployerInviteBypass } from '@/lib/employerInviteBypass';

const AuthContext = createContext(null);

/**
 * Map raw Supabase error messages to clear, non-technical language.
 * Also flags whether the error is specifically about an unverified email,
 * so the UI can offer a "Resend confirmation" action.
 */
function parseAuthError(rawMessage) {
  const msg = (rawMessage || '').toLowerCase();
  const isEmailUnverified =
    msg.includes('email not confirmed') || msg.includes('email_not_confirmed');

  let friendly;
  if (isEmailUnverified) {
    friendly = 'Please verify your email address before signing in. Check your inbox for a confirmation link.';
  } else if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials')) {
    friendly = 'The email or password you entered is incorrect.';
  } else if (msg.includes('user already registered') || msg.includes('already been registered')) {
    friendly = 'An account with this email already exists. Try signing in instead.';
  } else if (msg.includes('email rate limit') || msg.includes('over_email_send_rate_limit')) {
    friendly = 'Too many attempts. Please wait a few minutes before trying again.';
  } else if (msg.includes('password') && msg.includes('at least')) {
    friendly = rawMessage; // keep Supabase's own password-length message
  } else if (msg.includes('body stream')) {
    friendly = 'Unable to complete the request. Please try again.';
  } else {
    friendly = rawMessage || 'Something went wrong. Please try again.';
  }

  return { friendly, isEmailUnverified };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  // RC 1.38.B fix + RC 1.38.D-hotfix + RC 1.38.D2-hotfix (2026-04-26).
  //
  // Gates initial route paint and *re-gates only on fresh-identity*
  // events. Background-refresh events (TOKEN_REFRESHED, USER_UPDATED)
  // do NOT touch the gate — same user, same `hasProfile` answer,
  // no reason to unmount whichever protected page is rendered.
  //
  //   • Initial mount → starts `false`. The first
  //     `checkOnboardingComplete()` run flips it to `true` once
  //     XHRs resolve.
  //   • SIGNED_IN / INITIAL_SESSION → re-close the gate
  //     (`gate: true`) — `hasProfile` is genuinely stale.
  //   • TOKEN_REFRESHED / USER_UPDATED → DO NOT re-gate
  //     (`gate: false`). Same identity, same answer.
  //   • `refreshProfile()` from a child page → also `gate: false`.
  //
  // This shape prevents the live auth-loop captured 2026-04-26
  // (P0 onboarding wizard reset on CV upload) where every
  // TOKEN_REFRESHED event was unmounting OnboardingPage and
  // resetting `currentStep` to 0.
  const [profileChecked, setProfileChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const initialised = useRef(false);
  // RC 1.40-hotfix (2026-04-26) — gate-stability ref.
  //
  // iOS Safari + Android Chrome re-fire `INITIAL_SESSION` (and
  // sometimes `SIGNED_IN`) when a tab returns from a suspended
  // state — typically when the OS file picker closes after a CV
  // upload. The previous logic re-gated on every fresh-identity
  // event, which made `ProtectedRoute` return `null` while
  // `hasProfile` was being re-resolved, unmounting the upload
  // component AND its `<input type="file">`. The picked file
  // landed on a dead DOM node and the user's diagnostic panel
  // "started from the beginning."
  //
  // We now only re-close the gate when the user IDENTITY actually
  // changed since the last resolution. Same user → re-resolution
  // happens in the background with the gate left OPEN → upload
  // component stays mounted → `change` event from the file input
  // fires correctly → upload completes.
  const resolvedForUserRef = useRef(null);
  // RC 1.39.B-hotfix (2026-04-26) — `splashHoldExpired` removed.
  // All splash screens are gone from the system; the gate that
  // depended on this flag has been replaced with `return null`
  // during the (now imperceptible) bootstrap window.

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user) {
          // RC 1.49.x Slice 2 — same-identity guard.
          //
          // Supabase JS auto-refreshes the access token on tab return
          // and emits TOKEN_REFRESHED (and sometimes a re-emitted
          // INITIAL_SESSION). The SDK constructs a fresh `session.user`
          // object on every refresh — so calling `setUser(session.user)`
          // unconditionally was producing a NEW reference every time,
          // even when the identity (id + email) had not changed. That
          // reference change cascaded through every `useAuth()`
          // consumer (and through `WorkspaceContext.reload`'s
          // useCallback([user, hasProfile]) dependency chain), causing
          // the visible "page repaints on tab return" symptom Antonia
          // reported.
          //
          // Returning the prior reference for same-identity refreshes
          // lets React skip the bailout and prevents the cascade.
          // Genuinely new identities (login as different user) still
          // flow through normally because their id/email differ.
          setUser((prev) => {
            if (
              prev
              && prev.id === session.user.id
              && prev.email === session.user.email
            ) {
              return prev;
            }
            return session.user;
          });
          // RC 1.45.PRELAUNCH — identify the user in PostHog using the
          // stable Supabase UUID (never email). No-op without consent.
          identify(session.user.id);
          // Small delay for INITIAL_SESSION to let Supabase SDK finish
          // internal token operations before we make DB queries
          if (_event === 'INITIAL_SESSION') {
            await new Promise(r => setTimeout(r, 500));
          }
          // RC 1.38.D2-hotfix (2026-04-26) — narrow gate.
          //
          // Only fresh-identity auth events should re-close the
          // route gate. TOKEN_REFRESHED and USER_UPDATED keep the
          // same user; the previous `hasProfile` answer is still
          // valid for the routing decision. Re-gating on every
          // event was causing OnboardingPage to unmount/remount
          // whenever the Supabase JS SDK refreshed the access
          // token (which can happen mid-upload), resetting the
          // wizard to Step 1.
          //
          // The SELF-ACCESS POST that previously lived inline here
          // has been moved into a dedicated useEffect (see
          // `pending-role` effect below) so it does not run inside
          // this callback. That removed the inner-loop trigger
          // entirely (`apiFetch` → `getSession()` →
          // TOKEN_REFRESHED → callback → repeat).
          const FRESH_IDENTITY_EVENTS = new Set([
            'SIGNED_IN', 'INITIAL_SESSION',
          ]);
          // RC 1.40-hotfix — only re-close the gate when this event
          // truly represents a different user from the one whose
          // hasProfile we already resolved. Same user (which is the
          // typical iOS Safari "wake from picker" case) → background
          // refresh, gate stays open, upload component stays mounted.
          const sameUserAsResolved =
            resolvedForUserRef.current === session.user.id;
          const gate =
            FRESH_IDENTITY_EVENTS.has(_event) && !sameUserAsResolved;
          await checkOnboardingComplete(
            session.user.id, session.access_token, { gate },
          );
          resolvedForUserRef.current = session.user.id;
        } else {
          setUser(null);
          setHasProfile(false);
          // RC 1.45.PRELAUNCH — clear PostHog session on sign-out so we
          // never merge anonymous → identified across users.
          // Phase-1b — reset only on genuine SIGNED_OUT or a stale identified
          // PostHog state; logged-out page loads keep the anonymous identity.
          let staleIdentified = false;
          try { staleIdentified = window.posthog?.get_property?.('$user_state') === 'identified'; } catch { /* never throw */ }
          if (_event === 'SIGNED_OUT' || staleIdentified) resetAnalytics();
          // RC 1.45.PLAN-EVENTS — clear once-per-session telemetry flags
          // so the next user (or the same user after re-login) fires a
          // fresh onboarding_completed / plan_activated / first-action.
          try {
            sessionStorage.removeItem('ndor_onboarding_completed_fired');
            sessionStorage.removeItem('ndor_plan_activated_fired');
            sessionStorage.removeItem('ndor_dashboard_first_action_fired');
          } catch { /* never throw */ }
          // RC 1.40-hotfix — clear the resolved-user ref so the next
          // sign-in (potentially a different identity) is treated
          // as fresh and properly re-gates.
          resolvedForUserRef.current = null;
          // No user → nothing to check; PublicRoute renders /login.
          setProfileChecked(true);
          // RC 1.38.D2-hotfix — clear any persisted onboarding
          // wizard state on sign-out so the next user starts clean.
          try {
            for (const k of Object.keys(sessionStorage)) {
              if (k.startsWith('ndor-onboarding-')) sessionStorage.removeItem(k);
            }
          } catch { /* sessionStorage unavailable in some test envs */ }
        }
        if (!initialised.current) {
          initialised.current = true;
          setLoading(false);
        }
      }
    );

    // Safety-net: resolve loading state even if onAuthStateChange never fires
    const timeout = setTimeout(() => {
      if (!initialised.current) { initialised.current = true; setLoading(false); }
    }, 5000);

    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  // RC 1.38.D2-hotfix — pending-role self-access application.
  //
  // Previously this lived inline inside `onAuthStateChange`, but
  // `apiFetch` calls `supabase.auth.getSession()` which the
  // Supabase JS SDK uses as an opportunity to proactively refresh
  // the access token. That refresh fires TOKEN_REFRESHED → which
  // re-fired the auth callback → which re-fired apiFetch → which
  // re-triggered TOKEN_REFRESHED → infinite recursion (captured
  // live in backend logs as a tight stream of POSTs to
  // `/api/auth/me/self-access`).
  //
  // Moving it to a useEffect gated by `[user.id, role]` makes it:
  //   • Idempotent — fires at most once per (user, role) pair.
  //   • Async-safe — outside the auth-state-change callback, so
  //     any internal getSession()/refresh side-effects cannot
  //     cause the callback itself to re-fire and recurse.
  //   • Cancellable — uses an `alive` flag.
  const appliedRoleRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id) return;
    const pendingRole = user?.user_metadata?.role;
    if (pendingRole !== 'candidate' && pendingRole !== 'employer') return;
    const key = `${user.id}::${pendingRole}`;
    if (appliedRoleRef.current.has(key)) return;
    appliedRoleRef.current.add(key);
    let alive = true;
    apiFetch('/api/auth/me/self-access', {
      method: 'POST',
      body: { role: pendingRole },
    }).catch((err) => {
      if (!alive) return;
      // Backend is one-shot-guarded (409 once consents exist) so
      // a 409 here is normal for returning users.
      if (err?.status !== 409) {
        console.warn('[AuthContext] self-access apply failed:', err?.message);
      }
    });
    return () => { alive = false; };
  }, [user?.id, user?.user_metadata?.role]);


  /**
   * A user's onboarding is complete when they have BOTH:
   *   1. A row in public.profiles
   *   2. At least one row in public.consents
   *
   * This prevents a user who only completed step 1 from being routed
   * to /dashboard prematurely.
   */
  async function checkOnboardingComplete(userId, token, { gate = false } = {}) {
    console.log('[AuthContext] checkOnboardingComplete called for:', userId, 'hasToken:', !!token, 'gate:', gate);
    // RC 1.38.D-hotfix — selective gate.
    //   gate=true  → callers that mark a fresh auth transition (SIGNED_IN /
    //                INITIAL_SESSION / TOKEN_REFRESHED). The existing
    //                `hasProfile` is stale, so we re-close the route gate
    //                until the new check resolves. This prevents the
    //                "completed user briefly Navigated to /onboarding"
    //                flicker that RC 1.38.B was meant to solve.
    //   gate=false → background refreshes (`refreshProfile()` from a
    //                child page or from a background event). The
    //                gate stays open so the currently-rendered child
    //                does not unmount mid-render — that was the
    //                exact remount loop that bricked /signup before
    //                this hot-fix.
    if (gate) setProfileChecked(false);
    try {
      // Get token from param or from session
      let accessToken = token;
      if (!accessToken) {
        const { data: { session } } = await supabase.auth.getSession();
        accessToken = session?.access_token;
      }
      if (!accessToken) {
        // RC 1.46.HASPROFILE-STABILITY (2026-05-02) — do NOT flip
        // hasProfile to false on a transient "no access token"
        // race. Flipping false here caused the operator-reported
        // "Upload your Source CV" flash: the route gate redirects
        // to /onboarding the moment hasProfile goes false, even
        // though the user has a complete profile + consents on
        // record. Preserve the prior value; the next
        // onAuthStateChange or refreshProfile() call will retry.
        console.warn('[AuthContext] No access token available — preserving prior hasProfile');
        return;
      }

      const supaUrl = process.env.REACT_APP_SUPABASE_URL;
      const supaKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      // Use raw XHR to bypass the telemetry fetch interceptor and
      // Supabase client timing issues during INITIAL_SESSION.
      //
      // RC 1.46.HASPROFILE-STABILITY (2026-05-02) — `xhrGet` now
      // returns `null` on transport errors (timeout, network
      // failure, non-2xx HTTP, JSON parse failure) instead of `[]`.
      // The caller distinguishes "transport failed → preserve
      // prior hasProfile" from "row genuinely missing → flip to
      // false". Pre-fix, all failures collapsed to `[]` →
      // `hasProfile=false` → /onboarding flicker on every Supabase
      // 5xx or 6-second timeout.
      const xhrGet = (url) => new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('apikey', supaKey);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.timeout = 6000;
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); } catch { resolve(null); }
          } else {
            resolve(null);
          }
        };
        xhr.onerror = () => resolve(null);
        xhr.ontimeout = () => resolve(null);
        xhr.send();
      });

      const [profiles, consents] = await Promise.all([
        xhrGet(`${supaUrl}/rest/v1/profiles?id=eq.${userId}&select=id,employer_access,candidate_access&limit=1`),
        xhrGet(`${supaUrl}/rest/v1/consents?user_id=eq.${userId}&select=id&limit=1`),
      ]);

      // RC 1.46.HASPROFILE-STABILITY — if EITHER call failed at the
      // transport layer, we cannot distinguish "row missing" from
      // "Supabase timed out". Preserve the prior value; do not
      // flicker the route gate. The next onAuthStateChange or
      // refreshProfile() call will retry cleanly.
      if (profiles === null || consents === null) {
        console.warn('[AuthContext] checkOnboardingComplete: transport error — preserving prior hasProfile');
        return;
      }

      const hasP = Array.isArray(profiles) && profiles.length > 0;
      const hasC = Array.isArray(consents) && consents.length > 0;
      // RC 1.62 employer-auth doctrine (F-D6, 2026-05-29) —
      // Employer-only users skip the consents gate at the route
      // level. Doctrine: employer signups must never see the
      // org-create wizard. The signup choice on /signup is
      // already an explicit Employer-Terms acknowledgement; the
      // wizard that previously collected consents was bundled
      // with org-creation and is now admin-only. Consents-as-a-
      // separate-step is a future slice; the immediate fix is
      // that hasProfile must NOT block employer signups from
      // landing on /employer (workspace or stub). Candidates
      // still require BOTH rows because their flow records the
      // consents during the candidate onboarding wizard, which
      // continues to function as before.
      const employerOnly =
        hasP
        && Array.isArray(profiles) && profiles.length > 0
        && profiles[0]?.employer_access === true
        && profiles[0]?.candidate_access !== true;
      const result = employerOnly ? hasP : (hasP && hasC);
      console.log('[AuthContext] hasProfile result:', result, '| profile:', hasP, '| consent:', hasC, '| employerOnly:', employerOnly);
      // RC 1.45.PLAN-EVENTS — fire onboarding_completed exactly once
      // per session when we observe the false→true transition. Uses
      // the functional setter form to read the previous value
      // atomically and avoid a stale-closure double-fire.
      setHasProfile((prev) => {
        if (result && prev === false) {
          try {
            const k = 'ndor_onboarding_completed_fired';
            if (sessionStorage.getItem(k) !== '1') {
              track('onboarding_completed');
              sessionStorage.setItem(k, '1');
            }
          } catch { /* never throw from telemetry */ }
        }
        return result;
      });
    } catch (err) {
      // RC 1.46.HASPROFILE-STABILITY (2026-05-02) — on unexpected JS
      // errors (not transport — those are handled above via `null`
      // returns), preserve the prior hasProfile value rather than
      // flipping to false. A JS exception during the profile check
      // is a bug, not a sign the user has no profile. The route
      // gate stays on the last-known-good answer; the next event
      // cycle retries.
      console.warn('[AuthContext] checkOnboardingComplete failed — preserving prior hasProfile:', err.message);
    } finally {
      // RC 1.38.D-hotfix — open (or re-open) the route-redirect
      // gate now that we have at least one resolved answer. If
      // this run was non-gating (`gate=false`), the gate was
      // already open and this is a harmless no-op.
      setProfileChecked(true);
    }
  }

  /**
   * Sign up a new user.
   *
   * RC 1.20A: accepts optional `{ first_name, last_name, nickname }` and
   * upserts the row into `public.profiles` immediately after the auth
   * user is created. The UI assumes email confirmation is DISABLED at
   * the Supabase project level; if a session comes back the user is
   * logged in right away, otherwise the onboarding flow will surface
   * it.
   */
  // RC 1.49.x Slice 2 — useCallback wrappers (Fix A).
  // The exposed auth methods are recreated on every render of
  // `AuthProvider` unless wrapped. Combined with the non-memoised
  // provider-value below they were a major cause of consumer
  // re-renders on tab return. Each wrapper references state via
  // setters or supabase.auth (a stable module-level export), so
  // their dependency arrays are intentionally minimal.
  const signUp = useCallback(async (email, password, extras = {}) => {
    const { first_name, last_name, nickname, role } = extras;
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: first_name || null,
          last_name: last_name || null,
          nickname: nickname || null,
          // Carry the role choice through email-confirmation: it
          // lives on the Supabase auth.users row as user_metadata
          // until the user first logs in, at which point the
          // frontend calls /api/auth/me/self-access to apply it to
          // the profiles row.
          role: role || null,
        },
      },
    });
    if (result.error) {
      const { friendly } = parseAuthError(result.error.message);
      result.error = { ...result.error, message: friendly };
      return result;
    }

    // Upsert the profile row so the greeting has identity fields from
    // the very first /dashboard render — independent of onboarding.
    const userId = result.data?.user?.id;
    const session = result.data?.session;
    if (userId && session?.access_token) {
      try {
        const trimmedFirst = (first_name || '').trim();
        const trimmedLast  = (last_name  || '').trim();
        const full = [trimmedFirst, trimmedLast].filter(Boolean).join(' ') || null;
        await supabase.from('profiles').upsert(
          {
            id: userId,
            first_name: trimmedFirst || null,
            last_name:  trimmedLast  || null,
            nickname:   (nickname || '').trim() || null,
            full_name:  full,
          },
          { onConflict: 'id' },
        );
      } catch (err) {
        console.warn('[AuthContext] profile upsert after signup failed:', err?.message || err);
      }

      // Apply the role choice server-side. The backend enforces the
      // canonical access flags + default_workspace mapping — the
      // client is never trusted to send them directly.
      if (role === 'candidate' || role === 'employer') {
        try {
          await apiFetch('/api/auth/me/self-access', {
            method: 'POST',
            body: { role },
          });
        } catch (err) {
          console.warn('[AuthContext] self-access apply failed:', err?.message || err);
        }
      }
    }
    return result;
  }, []);

  /**
   * Sign in with email + password.
   * Returns a `_isEmailUnverified` flag alongside the friendly message
   * so the login page can offer a "Resend confirmation" action.
   */
  const signIn = useCallback(async (email, password) => {
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) {
      const { friendly, isEmailUnverified } = parseAuthError(result.error.message);
      result.error = { ...result.error, message: friendly, _isEmailUnverified: isEmailUnverified };
    }
    return result;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      // Phase 0.7.2 — make sure the invitee bypass marker doesn't
      // leak across user sessions in the same tab.
      clearEmployerInviteBypass();
      setUser(null); setHasProfile(false);
    }
    return { error };
  }, []);

  /**
   * Resend the signup confirmation email via Supabase's auth.resend() API.
   * Returns { error } — null on success.
   */
  const resendConfirmation = useCallback(async (email) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) {
      const { friendly } = parseAuthError(error.message);
      return { error: { message: friendly } };
    }
    return { error: null };
  }, []);

  /**
   * Re-check onboarding completeness.
   * Called after the last onboarding step to trigger the route redirect.
   */
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const { data: { session } } = await supabase.auth.getSession();
    await checkOnboardingComplete(user.id, session?.access_token);
  }, [user]);

  // RC 1.49.x Slice 2 — memoised provider value (Fix A).
  // Pre-fix this was a fresh object literal on every render of
  // `AuthProvider`. Combined with `setUser` constantly producing
  // new references on TOKEN_REFRESHED, every Supabase auth event
  // detonated a re-render storm across the entire authenticated
  // tree. Memoising on the actual state inputs lets React's
  // context propagation skip consumers when nothing material has
  // changed.
  const value = useMemo(
    () => ({
      user, hasProfile, profileChecked, loading,
      signUp, signIn, signOut, resendConfirmation, refreshProfile,
    }),
    [
      user, hasProfile, profileChecked, loading,
      signUp, signIn, signOut, resendConfirmation, refreshProfile,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
